import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ShieldCheckIcon,
  CpuChipIcon,
  EyeIcon,
  UsersIcon,
  ChartBarIcon,
  ClockIcon,
  DocumentTextIcon,
  Cog6ToothIcon,
  PlayIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  FlagIcon,
  BellIcon,
  LinkIcon,
  XMarkIcon,
  CheckCircleIcon,
  SparklesIcon,
  FireIcon,
} from "@heroicons/react/24/outline";

const LandingPage = () => {
  const navigate = useNavigate();
  
  // State for quote modal
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [quoteForm, setQuoteForm] = useState({
    fullName: '',
    businessEmail: '',
    expectations: ''
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Handle form submission
  const handleQuoteSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    const errors = {};
    if (!quoteForm.fullName.trim()) errors.fullName = 'Full name is required';
    if (!quoteForm.businessEmail.trim()) errors.businessEmail = 'Business email is required';
    if (!/\S+@\S+\.\S+/.test(quoteForm.businessEmail)) errors.businessEmail = 'Please enter a valid email';
    if (!quoteForm.expectations.trim()) errors.expectations = 'Please describe your expectations';
    
    setFormErrors(errors);
    
    if (Object.keys(errors).length === 0) {
      // Submit form (in real app, this would call an API)
      console.log('Quote request submitted:', quoteForm);
      setShowSuccessMessage(true);
      setTimeout(() => {
        setShowQuoteModal(false);
        setShowSuccessMessage(false);
        setQuoteForm({ fullName: '', businessEmail: '', expectations: '' });
      }, 3000);
    }
  };

  const features = [
    {
      icon: ShieldCheckIcon,
      title: "Automated Compliance",
      description: "Streamline compliance workflows with automated evidence collection across 20+ major frameworks including SOC 2, ISO 27001, HIPAA, GDPR, and more with 100% uptime guarantee.",
      gradient: "from-brand to-brand-dark"
    },
    {
      icon: CpuChipIcon,
      title: "AI-Powered Insights",
      description: "Leverage advanced AI for smart compliance recommendations, risk analysis, and automated gap assessments with natural language processing.",
      gradient: "from-purple-600 to-pink-600"
    },
    {
      icon: EyeIcon,
      title: "Real-time Monitoring",
      description: "24/7 continuous monitoring with 100% uptime, live dashboards, predictive analytics, and intelligent alerting across 80+ compliance documents for proactive management.",
      gradient: "from-green-600 to-teal-600"
    },
    {
      icon: UsersIcon,
      title: "Team Collaboration",
      description: "Seamless team workflows with role-based access, task management, audit trails, and integrated communication tools.",
      gradient: "from-orange-600 to-red-600"
    }
  ];

  const modules = [
    {
      icon: ShieldCheckIcon,
      name: "Compliance Frameworks",
      description: "Comprehensive framework management covering 20+ standards including ISO 27001, SOC 2, HIPAA, PCI DSS, NIST CSF, Cyber Essentials, FedRAMP, HITRUST, ISO 27701, CIS Controls, COBIT, and ITIL with automated evidence collection, gap analysis, and real-time compliance scoring.",
      features: ["20+ Framework Support", "Automated Evidence Collection", "Real-time Scoring", "Gap Analysis"],
      color: "text-blue-400",
      gradient: "from-brand to-brand-dark"
    },
    {
      icon: FlagIcon,
      name: "Global Privacy",
      description: "Complete privacy regulation management for GDPR, CCPA, PDPA, PIPEDA, DPDPA, and emerging regulations with automated data mapping, consent management, breach notification workflows, and privacy impact assessments across global jurisdictions.",
      features: ["Multi-jurisdiction Support", "Data Mapping", "Consent Management", "Breach Workflows"],
      color: "text-green-400",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: BuildingOfficeIcon,
      name: "Third-Party Risk Management",
      description: "Advanced TPRM platform with intelligent vendor onboarding, automated SIG assessments, continuous risk monitoring, contract management, and supply chain risk analysis with AI-powered vendor scoring and predictive risk modeling.",
      features: ["Vendor Onboarding", "SIG Assessments", "Risk Monitoring", "AI Scoring"],
      color: "text-purple-400",
      gradient: "from-purple-500 to-violet-500"
    },
    {
      icon: DocumentTextIcon,
      name: "Document Library",
      description: "Centralized knowledge hub with 80+ compliance documents, intelligent policy management, automated version control, digital acknowledgments, role-based access controls, and AI-powered document recommendations with audit trails.",
      features: ["80+ Documents", "Version Control", "Digital Signatures", "AI Recommendations"],
      color: "text-cyan-400",
      gradient: "from-cyan-500 to-teal-500"
    },
    {
      icon: AcademicCapIcon,
      name: "Learning Management System",
      description: "Comprehensive compliance training platform with personalized learning paths, interactive simulations, competency assessments, certification tracking, and AI-driven content recommendations with real-time progress analytics.",
      features: ["Personalized Learning", "Interactive Simulations", "Certifications", "Progress Analytics"],
      color: "text-orange-400",
      gradient: "from-orange-500 to-amber-500"
    },
    {
      icon: BellIcon,
      name: "24/7 Monitoring & Alerts",
      description: "Continuous compliance monitoring with 100% uptime guarantee, intelligent alerting, predictive anomaly detection, automated remediation workflows, and real-time dashboard insights with escalation management and SLA tracking.",
      features: ["100% Uptime", "Predictive Detection", "Auto-remediation", "SLA Tracking"],
      color: "text-red-400",
      gradient: "from-red-500 to-rose-500"
    },
    {
      icon: LinkIcon,
      name: "Integration Hub",
      description: "Seamless ecosystem connectivity with 50+ platform integrations including ServiceNow, OneTrust, MetricStream, Qualys, Okta, and major cloud providers with pre-built connectors, API orchestration, and data synchronization.",
      features: ["50+ Integrations", "Pre-built Connectors", "API Orchestration", "Data Sync"],
      color: "text-indigo-400",
      gradient: "from-indigo-500 to-blue-500"
    },
    {
      icon: SparklesIcon,
      name: "AI-Powered Analytics",
      description: "Advanced artificial intelligence engine with machine learning models for compliance gap analysis, predictive risk assessment, intelligent automation, natural language processing, and continuous improvement recommendations.",
      features: ["ML Models", "Predictive Analysis", "NLP Processing", "Smart Automation"],
      color: "text-pink-400",
      gradient: "from-pink-500 to-fuchsia-500"
    },
    {
      icon: ChartBarIcon,
      name: "Executive Reporting",
      description: "Comprehensive business intelligence platform with dynamic executive dashboards, KPI tracking, predictive analytics, custom report builder, automated scheduling, and stakeholder-specific insights with drill-down capabilities.",
      features: ["Executive Dashboards", "Custom Reports", "Predictive Analytics", "Automated Scheduling"],
      color: "text-yellow-400",
      gradient: "from-yellow-500 to-orange-500"
    }
  ];

  const stats = [
    { number: "100%", label: "Uptime" },
    { number: "80+", label: "Compliance Documents" },
    { number: "20+", label: "Major Frameworks" },
    { number: "24/7", label: "Monitoring" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      {/* Navigation */}
      <nav className="relative z-50 p-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center space-x-3"
          >
            <div className="w-10 h-10 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-xl">T</span>
            </div>
            <span className="text-white text-2xl font-bold">Tanikkai</span>
          </motion.div>

          <div className="flex items-center space-x-4">
            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/website')}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-6 py-3 rounded-xl font-semibold hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg"
            >
              Complete Website
            </motion.button>

            <motion.button
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/login')}
              className="bg-white/10 backdrop-blur-sm border border-white/20 text-white px-6 py-3 rounded-xl font-semibold hover:bg-white/20 transition-all"
            >
              GRC Platform
            </motion.button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative px-6 pt-20 pb-32">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-8 leading-tight">
              <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">
                Unified Compliance
              </span>
              <br />
              Platform
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed">
              Enterprise-grade compliance management with 100% uptime, 80+ compliance documents, 
              20+ major frameworks, and 24/7 continuous monitoring with AI-powered automation.
            </p>

            {/* Hero Image Section - AI Core Engine Visualization */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="mb-16 relative"
            >
              <div className="max-w-4xl mx-auto relative">
                <div className="relative overflow-hidden rounded-3xl shadow-2xl border border-white/20">
                  <img 
                    src="https://images.unsplash.com/photo-1697577418970-95d99b5a55cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlfGVufDB8fHx8MTc1NDkyNjkwMHww&ixlib=rb-4.1.0&q=85" 
                    alt="Tanikkai AI Core Engine - Advanced AI and GPU Processing Technology"
                    className="w-full h-96 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-r from-cyan-900/70 via-blue-900/50 to-purple-900/70"></div>
                  
                  {/* Overlay Content */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.5 }}
                        className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-4"
                      >
                        <CpuChipIcon className="w-6 h-6 text-cyan-400 mr-2" />
                        <span className="text-white font-semibold">Powered by Advanced AI Technology</span>
                      </motion.div>
                      <motion.h3
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.6 }}
                        className="text-3xl font-bold text-white mb-2"
                      >
                        Tanikkai AI Core Engine
                      </motion.h3>
                      <motion.p
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                        className="text-gray-200 max-w-2xl"
                      >
                        Neural networks and machine learning algorithms driving intelligent compliance automation
                      </motion.p>
                    </div>
                  </div>
                </div>

                {/* Floating AI Features */}
                <div className="absolute -top-4 -left-4 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-2xl p-4 border border-white/20 backdrop-blur-sm">
                  <div className="flex items-center space-x-2">
                    <SparklesIcon className="w-5 h-5 text-white" />
                    <span className="text-white font-semibold text-sm">AI-Powered</span>
                  </div>
                </div>

                <div className="absolute -bottom-4 -right-4 bg-gradient-to-r from-purple-500 to-pink-600 rounded-2xl p-4 border border-white/20 backdrop-blur-sm">
                  <div className="flex items-center space-x-2">
                    <FireIcon className="w-5 h-5 text-white" />
                    <span className="text-white font-semibold text-sm">Real-time Processing</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16"
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.3 + index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-bold text-cyan-400 mb-2">
                  {stat.number}
                </div>
                <div className="text-gray-400 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="relative px-6 py-20 bg-black/20 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Powerful Features for Modern Compliance
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Everything you need to automate compliance, manage risks, and collaborate effectively
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02 }}
                className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-xl flex items-center justify-center mb-6`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-4">{feature.title}</h3>
                <p className="text-gray-300 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Platform Modules */}
      <section className="relative px-6 py-20">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Complete Compliance Platform
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Nine integrated modules covering every aspect of compliance management
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {modules.map((module, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                viewport={{ once: true }}
                whileHover={{ scale: 1.02, y: -5 }}
                className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10 hover:border-white/20 transition-all duration-300 hover:shadow-2xl hover:shadow-cyan-500/10"
              >
                <div className={`w-16 h-16 bg-gradient-to-r ${module.gradient} rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <module.icon className="w-8 h-8 text-white" />
                </div>
                
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-cyan-300 transition-colors">
                  {module.name}
                </h3>
                
                <p className="text-gray-300 text-sm leading-relaxed mb-4">
                  {module.description}
                </p>
                
                <div className="flex flex-wrap gap-2">
                  {module.features.map((feature, featureIndex) => (
                    <span
                      key={featureIndex}
                      className="px-3 py-1 bg-white/10 backdrop-blur-sm rounded-full text-xs text-cyan-300 border border-white/20"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
                
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center text-xs text-gray-400">
                    <FireIcon className="w-4 h-4 mr-1 text-orange-400" />
                    <span>AI-Powered & Enterprise Ready</span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Unified Platform Architecture Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="text-center mb-12">
              <h3 className="text-4xl font-bold text-white mb-4">
                Unified Platform Architecture
              </h3>
              <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
                Experience seamless integration across all compliance domains with our AI-powered central hub that 
                orchestrates intelligent workflows, real-time data synchronization, and automated decision-making 
                across your entire compliance ecosystem.
              </p>
            </div>

            <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm rounded-3xl p-12 border border-white/10 overflow-hidden">
              {/* Background Architecture Image - Updated with AI-generated image showing interconnected systems */}
              <div className="absolute inset-0 opacity-20">
                <img 
                  src="https://images.unsplash.com/photo-1674027444485-cec3da58eef4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlfGVufDB8fHx8MTc1NDkyNjkwMHww&ixlib=rb-4.1.0&q=85" 
                  alt="AI-Powered Unified Platform Architecture with Interconnected Systems"
                  className="w-full h-full object-cover rounded-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-900/60 to-purple-900/60 rounded-2xl"></div>
              </div>

              <div className="relative min-h-[600px]">
                {/* Central AI Hub */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <motion.div
                    initial={{ scale: 0, opacity: 0 }}
                    whileInView={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.8, delay: 0.5 }}
                    viewport={{ once: true }}
                    className="relative"
                  >
                    <div className="w-40 h-40 bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-2xl shadow-cyan-500/50 animate-pulse">
                      <div className="w-32 h-32 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-full flex items-center justify-center">
                        <div className="text-center">
                          <SparklesIcon className="w-10 h-10 text-white mx-auto mb-2" />
                          <span className="text-white font-bold text-lg">Tanikkai</span>
                          <div className="text-sm text-cyan-100">AI Core Engine</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Pulsing rings */}
                    <div className="absolute inset-0 rounded-full border-2 border-cyan-400/30 animate-ping"></div>
                    <div className="absolute inset-0 rounded-full border border-blue-400/20 animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                  </motion.div>
                </div>

                {/* Enhanced Surrounding Modules */}
                <div className="relative grid grid-cols-4 gap-6 p-12">
                  {[
                    { icon: ShieldCheckIcon, name: "Compliance Frameworks", color: "from-brand to-brand-dark", position: "top-left" },
                    { icon: FlagIcon, name: "Global Privacy", color: "from-green-500 to-emerald-500", position: "top-center" },
                    { icon: BuildingOfficeIcon, name: "TPRM", color: "from-purple-500 to-violet-500", position: "top-right" },
                    { icon: DocumentTextIcon, name: "Document Library", color: "from-cyan-500 to-teal-500", position: "center-left" },
                    { icon: BellIcon, name: "24/7 Monitoring", color: "from-red-500 to-rose-500", position: "center-right" },
                    { icon: AcademicCapIcon, name: "LMS Training", color: "from-orange-500 to-amber-500", position: "bottom-left" },
                    { icon: LinkIcon, name: "Integration Hub", color: "from-indigo-500 to-blue-500", position: "bottom-center" },
                    { icon: SparklesIcon, name: "AI Analytics", color: "from-pink-500 to-fuchsia-500", position: "bottom-center-2" },
                    { icon: ChartBarIcon, name: "Executive Reporting", color: "from-yellow-500 to-orange-500", position: "bottom-right" }
                  ].map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0, rotate: 180 }}
                      whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                      transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                      viewport={{ once: true }}
                      whileHover={{ scale: 1.1, z: 10 }}
                      className={`relative w-24 h-24 bg-gradient-to-r ${item.color} rounded-2xl flex flex-col items-center justify-center shadow-xl hover:shadow-2xl transition-all duration-300 cursor-pointer group`}
                      style={{
                        gridColumn: index < 3 ? index + 1 : index === 3 ? 1 : index === 4 ? 4 : index < 7 ? index - 4 : index === 7 ? 2 : 3,
                        gridRow: index < 3 ? 1 : index < 5 ? 2 : index < 7 ? 4 : 3
                      }}
                    >
                      <item.icon className="w-7 h-7 text-white mb-1 group-hover:scale-110 transition-transform" />
                      <span className="text-xs text-white font-semibold text-center leading-tight px-1">
                        {item.name}
                      </span>
                      
                      {/* Connecting line to center */}
                      <motion.div
                        initial={{ scale: 0 }}
                        whileInView={{ scale: 1 }}
                        transition={{ duration: 0.5, delay: 1.5 + index * 0.05 }}
                        viewport={{ once: true }}
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                      >
                        <div className="w-px h-32 bg-gradient-to-b from-white/40 to-transparent transform origin-bottom rotate-45"></div>
                      </motion.div>
                    </motion.div>
                  ))}
                </div>

                {/* Data Flow Indicators */}
                <div className="absolute inset-0 pointer-events-none">
                  {Array.from({ length: 9 }, (_, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0 }}
                      whileInView={{ opacity: [0, 1, 0] }}
                      transition={{
                        duration: 2,
                        delay: 2 + i * 0.2,
                        repeat: Infinity,
                        repeatDelay: 3
                      }}
                      viewport={{ once: true }}
                      className="absolute w-2 h-2 bg-cyan-400 rounded-full"
                      style={{
                        left: `${50 + Math.cos((i * 40 - 160) * Math.PI / 180) * 20}%`,
                        top: `${50 + Math.sin((i * 40 - 160) * Math.PI / 180) * 20}%`,
                        transform: 'translate(-50%, -50%)'
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Architecture Benefits */}
              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-12 h-12 bg-gradient-to-r from-brand to-brand-dark rounded-full flex items-center justify-center mx-auto mb-4">
                    <CpuChipIcon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">Intelligent Orchestration</h4>
                  <p className="text-gray-300 text-sm">AI-powered workflow automation with real-time decision making across all modules</p>
                </div>
                
                <div className="text-center p-6 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <LinkIcon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">Seamless Integration</h4>
                  <p className="text-gray-300 text-sm">Unified data layer with real-time synchronization and cross-module intelligence</p>
                </div>
                
                <div className="text-center p-6 bg-white/5 rounded-xl border border-white/10">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <EyeIcon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-white mb-2">360° Visibility</h4>
                  <p className="text-gray-300 text-sm">Comprehensive compliance posture with predictive insights and risk analytics</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative px-6 py-20 bg-gradient-to-r from-cyan-600/20 to-blue-600/20 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Compliance?
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              Join leading organizations using Tanikkai to automate compliance, reduce risk, and scale securely.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowQuoteModal(true)}
              className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white px-12 py-4 rounded-xl font-semibold text-lg shadow-xl hover:shadow-2xl transition-all"
            >
              Get Quote
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Quote Modal */}
      <AnimatePresence>
        {showQuoteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-md w-full p-8 shadow-2xl"
            >
              {!showSuccessMessage ? (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-bold text-gray-900">Get Your Custom Quote</h3>
                    <button
                      onClick={() => setShowQuoteModal(false)}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <XMarkIcon className="w-6 h-6" />
                    </button>
                  </div>

                  <form onSubmit={handleQuoteSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        id="fullName"
                        type="text"
                        value={quoteForm.fullName}
                        onChange={(e) => setQuoteForm({ ...quoteForm, fullName: e.target.value })}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          formErrors.fullName ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Enter your full name"
                      />
                      {formErrors.fullName && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.fullName}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="businessEmail" className="block text-sm font-medium text-gray-700 mb-2">
                        Business Email ID *
                      </label>
                      <input
                        id="businessEmail"
                        type="email"
                        value={quoteForm.businessEmail}
                        onChange={(e) => setQuoteForm({ ...quoteForm, businessEmail: e.target.value })}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          formErrors.businessEmail ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="Enter your business email"
                      />
                      {formErrors.businessEmail && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.businessEmail}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="expectations" className="block text-sm font-medium text-gray-700 mb-2">
                        Your Expectations *
                      </label>
                      <textarea
                        id="expectations"
                        rows={4}
                        value={quoteForm.expectations}
                        onChange={(e) => setQuoteForm({ ...quoteForm, expectations: e.target.value })}
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          formErrors.expectations ? 'border-red-300' : 'border-gray-300'
                        }`}
                        placeholder="e.g., ISO 27001 Automation & Privacy Compliance"
                      />
                      {formErrors.expectations && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.expectations}</p>
                      )}
                    </div>

                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-cyan-600 hover:to-blue-700 transition-all shadow-lg"
                    >
                      Submit Request
                    </motion.button>
                  </form>
                </>
              ) : (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-center py-8"
                >
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircleIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Thanks for your interest in Tanikkai!
                  </h3>
                  <p className="text-gray-600">
                    Our team will contact you shortly with a custom quote tailored to your compliance needs.
                  </p>
                </motion.div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Footer */}
      <footer className="relative px-6 py-12 border-t border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <div className="w-8 h-8 bg-gradient-to-r from-cyan-400 to-blue-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">T</span>
            </div>
            <span className="text-white text-xl font-bold">Tanikkai</span>
          </div>
          <p className="text-gray-400">
            © 2025 Tanikkai. Enterprise-grade compliance automation platform.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;